import { Application } from './Application';
import { Language } from './Language';
import { SessionTypeEnum } from './Session';

export type KnowledgeBaseLinkedIdArticleOptions = Array<{ id: number; name: string }>;

export type KnowledgeBaseDirectoryTranslation = {
	languageCode: string;
	text: string;
	slug: string;
};

export type KnowledgeBaseDirectoryMeta = {
	id: number;
	label: string | null;
	name: string;
	slug: string;
};

export type KnowledgeBaseDirectory = {
	id: number;
	label: string | null;
	info: {
		isSystem: boolean;
		articles: number;
		displayInHomepage?: boolean;
	};
	translations?: KnowledgeBaseDirectoryTranslation[];
};

export type KnowledgeBaseDirectoryTranslated = KnowledgeBaseDirectory & { name: string; slug: string };

export type KnowledgeBaseTag = {
	id: number;
	label: string | null;
};

export type KnowledgeBaseTagTranslated = KnowledgeBaseTag & { name: string; slug: string };

export enum KnowledgeBaseArticleContentType {
	PDF = 'pdf',
	HTML = 'html',
	HTML_WYSWIG = 'html_wyswig',
	HTML_WYSWIG_2 = 'html_wyswig_2',
	HTML_BLOCKS = 'html_blocks',
	LINK = 'link',
	VIDEO = 'video',
	FILE = 'file',
	FORM = 'form',
}

export enum KnowledgeBaseArticleMaterialCategory {
	TOOL = 'tool',
	EXERCISE = 'exercise',
	KNOWLEDGE = 'knowledge',
	PDF = 'pdf',
	VIDEO = 'video',
	FORM = 'form',
	NOTE = 'note',
	OTHER = 'other',
}

export type KnowledgeBaseArticleVisitDetails = {
	application: Application;
	visits: number;
	firstVisitAt: Date;
	applicationRating: {
		rating: number;
		comment: string | null;
	} | null;
};

export type KnowledgeBaseArticleInfo = {
	visits: {
		mentee: number;
		mentor: number;
		all: number;
	};
	ratings: {
		positive: number;
		negative: number;
	};
};

export type KnowledgeBaseArticle = {
	id: number;
	directory?: KnowledgeBaseDirectory;
	directoryId: number;
	contentType: KnowledgeBaseArticleContentType;
	menteeAccess: boolean;
	mentorAccess: boolean;
	sessionType: SessionTypeEnum | null;
	sessionNumber: number | null;
	featured: boolean;
	mentibot: boolean;
	label: string | null;
	category: KnowledgeBaseArticleMaterialCategory | null;
	createdAt: Date;
	updatedAt: Date | null;
	tags?: KnowledgeBaseTag[];

	contents: KnowledgeBaseArticleContent[];
	isProfessional: boolean;
};

export type KnowledgeBaseArticleContentItemMeta = {
	id: number;
	label: string | null;
	title: string;
	slug: string;
};

export type KnowledgeBaseArticleFlags = {
	readed: boolean;
	favorite: boolean;
	toRead: boolean;
};

export type KnowledgeBaseArticleContentItem = {
	id: number;
	label: string | null;
	articleId: number;
	title: string;
	slug: string;
	contentType: KnowledgeBaseArticleContentType;
	usage?: string | null;
	info: {
		isSystem: boolean;
		access?: {
			mentee: boolean;
			mentor: boolean;
		};
		visits: number;
		isFallback: boolean;
		fallbackLanguageCode: string;
		flag: KnowledgeBaseArticleFlags;
		timeToRead?: number;
		imageUrl?: string | null;
		createdAt: Date;
		link?: string;
	};
	category: KnowledgeBaseArticleMaterialCategory | null;
	isProfessional: boolean;
	//
	article?: KnowledgeBaseArticle;
};

export type KnowledgeBaseArticleContent = {
	id: number;
	articleId: number;

	title: string;
	slug: string;
	languageCode: string;
	url: string | null;
	storageUrl: string | null;
	originalFilename: string | null;
	contentText: string | null;
	contentData: any | null;
	contentBlocks: any | null;
	createdAt: Date;
	updatedAt: Date | null;
	//
	article?: KnowledgeBaseArticle;
	signedUrl?: string;
	signedUrlAlternative?: string;
	content?: string;

	// from view
	fallbackLanguageCode?: string;

	keywords?: Array<{ id: number; text: string }>;
};

export type KnowledgeBaseArticlePresentationResult = {
	article: KnowledgeBaseArticle;
	articleContent: Omit<KnowledgeBaseArticleContent, 'article'>;
	directory: KnowledgeBaseDirectoryTranslated | null;
	alternates: Array<{ articleId: number; slug: string; language: Language }>;
	info: {
		isSystem: boolean;
		visitId: number | null;
		flag: KnowledgeBaseArticleFlags;
	};
};

export type KnowledgeBaseArticleAdminResult = {
	article: KnowledgeBaseArticle;
	articleContent: KnowledgeBaseArticleContent;
	item: KnowledgeBaseArticleContentItem;
};

export type KnowledgeBaseArticleRating = {
	id: number;
	rating: number;
	comment: string | null;
	createdAt: Date;
	updatedAt: Date | null;
};

export type KnowledgeBaseDirectoryLanguagesResult = {
	program: Language;
	optional: Language[];
};

export type KnowledgeBaseArticleShortInfo = {
	id: number;
	title: string;
	label: string;
	slug: string;
};

export type KnowledgeBaseRecommendedArticlesByCategory = Array<{
	categoryId: number;
	slug: string;
	name: string;
	articles: KnowledgeBaseArticleContentItem[];
}>;

export type KnowledgeBaseFlagedArticles = {
	favorites: {
		items: KnowledgeBaseArticleContentItem[];
		count: number;
	};
	readed: {
		items: KnowledgeBaseArticleContentItem[];
		count: number;
	};
	toRead: {
		items: KnowledgeBaseArticleContentItem[];
		count: number;
	};
};

export type KnowledgeBaseTagGroup = {
	id: number;
	label: string;
	name: string;
	slug: string;
	tags: KnowledgeBaseTagTranslated[];
};
